import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge, EmptyState, ErrorNote } from '../ui';

interface TemplateRow {
  id: string;
  name: string;
  category: string;
  originalFilename: string | null;
  activeVersionId: string | null;
  versions: { id: string; version: number; body: string; changeNote: string | null; createdAt: string }[];
}

/** The six supplied template files map to these names on import (spec §13). */
const BUILTIN_HINTS: Record<string, string> = {
  titlegenerator: 'title-generation',
  captiongenerator: 'reel-caption',
  promptjson: 'reel-video-json',
  capcutoverlay: 'overlay-text',
  referenceimage: 'reference-image',
  thumbnailprompt: 'thumbnail',
};

const PROVIDER_TASKS = [
  ['image-topic-research', 'Image post topics'],
  ['image-title-generation', 'Image post titles'],
  ['image-caption-generation', 'Image post captions'],
  ['content-review', 'Content review'],
  ['reel-title-generation', 'Reel concepts'],
  ['reel-conversation', 'Reel scripts'],
  ['reel-caption-generation', 'Reel captions'],
  ['reel-reference-image', 'Reference-image prompt'],
  ['reel-thumbnail', 'Thumbnail prompt'],
] as const;

export function PromptLibraryScreen() {
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [selected, setSelected] = useState<TemplateRow | null>(null);
  const [editorBody, setEditorBody] = useState('');
  const [providerPreferences, setProviderPreferences] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setTemplates((await window.pagepilot.prompts.list()) as TemplateRow[]);
      const preferences = (await window.pagepilot.prompts.listProviderPreferences()) as { purpose: string; providerKind: string | null }[];
      setProviderPreferences(Object.fromEntries(preferences.map((preference) => [preference.purpose, preference.providerKind ?? 'claude-cli'])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, []);

  const open = async (id: string) => {
    const t = (await window.pagepilot.prompts.get(id)) as TemplateRow;
    setSelected(t);
    const active = t.versions.find((v) => v.id === t.activeVersionId) ?? t.versions[0];
    setEditorBody(active?.body ?? '');
  };

  const importFiles = async (files: FileList) => {
    for (const file of Array.from(files)) {
      const body = await file.text();
      const base = file.name.toLowerCase().replace(/\(\d+\)/g, '').replace(/\.txt$/, '').trim();
      const category = BUILTIN_HINTS[base] ?? 'topic-generation';
      await window.pagepilot.prompts.import({
        name: base || file.name,
        body,
        category,
        originalFilename: file.name,
      });
    }
    void load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Prompt Library</h1>
        <div className="flex gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".txt,.md"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && void importFiles(e.target.files)}
          />
          <Button onClick={() => fileInput.current?.click()}>Import .txt templates</Button>
        </div>
      </div>
      {error && <ErrorNote message={error} />}

      <Card title="Text provider by generation task">
        <p className="mb-3 text-xs text-neutral-500">Choose the first provider for each task. If it cannot generate, PagePilot tries the other installed CLI automatically.</p>
        <div className="grid grid-cols-3 gap-3">
          {PROVIDER_TASKS.map(([purpose, label]) => (
            <label key={purpose} className="text-xs text-neutral-400">
              {label}
              <select
                value={providerPreferences[purpose] ?? 'claude-cli'}
                className="mt-1 block w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-neutral-200"
                onChange={async (event) => {
                  const providerKind = event.target.value as 'claude-cli' | 'codex-cli';
                  try {
                    await window.pagepilot.prompts.setProviderPreference(purpose, providerKind);
                    setProviderPreferences((current) => ({ ...current, [purpose]: providerKind }));
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
              >
                <option value="claude-cli">Claude Code</option>
                <option value="codex-cli">Codex</option>
              </select>
            </label>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-[320px_1fr] gap-4">
        <div className="space-y-2">
          {templates.length === 0 ? (
            <EmptyState
              title="No prompt templates yet"
              hint="Import the six supplied Reel template files (titlegenerator, captiongenerator, promptjson, capcutoverlay, referenceimage, thumbnailprompt) or create new ones."
            />
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                onClick={() => void open(t.id)}
                className={`block w-full rounded-lg border px-3 py-2 text-left ${
                  selected?.id === t.id ? 'border-brand-400 bg-brand-500/10' : 'border-neutral-800 bg-neutral-900/60 hover:border-neutral-700'
                }`}
              >
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-neutral-500">
                  {t.category} · v{t.versions[0]?.version ?? 1}
                  {t.originalFilename ? ` · from ${t.originalFilename}` : ''}
                </p>
              </button>
            ))
          )}
        </div>

        {selected ? (
          <Card title={`Edit: ${selected.name}`}>
            <textarea
              value={editorBody}
              onChange={(e) => setEditorBody(e.target.value)}
              spellCheck={false}
              className="h-[420px] w-full resize-none rounded-lg border border-neutral-700 bg-neutral-950 p-3 font-mono text-xs text-neutral-200 outline-none focus:border-brand-400"
            />
            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={async () => {
                  await window.pagepilot.prompts.saveVersion(selected.id, editorBody, 'Edited in Prompt Library');
                  await open(selected.id);
                  void load();
                }}
              >
                Save as new version
              </Button>
              <span className="text-xs text-neutral-500">
                {selected.versions.length} version{selected.versions.length === 1 ? '' : 's'}
              </span>
            </div>
            {selected.versions.length > 1 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold text-neutral-400">Revision history</p>
                <ul className="space-y-1">
                  {selected.versions.map((v) => (
                    <li key={v.id} className="flex items-center gap-2 text-xs text-neutral-400">
                      <Badge status={v.id === selected.activeVersionId ? 'ACTIVE' : 'ARCHIVED'} label={`v${v.version}`} />
                      <span>{new Date(v.createdAt).toLocaleString()}</span>
                      <span className="text-neutral-600">{v.changeNote}</span>
                      {v.id !== selected.activeVersionId && (
                        <button
                          className="text-brand-300 hover:underline"
                          onClick={async () => {
                            await window.pagepilot.prompts.rollback(selected.id, v.id);
                            await open(selected.id);
                          }}
                        >
                          Roll back to this
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        ) : (
          <EmptyState title="Select a template" hint="Pick a template on the left to view and edit it. Every save creates a new version; rollback is always available." />
        )}
      </div>
      <p className="text-xs text-neutral-600">
        Available variables: {'{{page.name}} {{page.country}} {{page.language}} {{page.niche}} {{content.topic}} {{reel.duration}} {{research.sources}}'} — unresolved variables are flagged before execution.
      </p>
    </div>
  );
}
